import { ExperimentOutlined, RightOutlined } from '@ant-design/icons'
import { useMutation } from '@tanstack/react-query'
import { Button, Flex, Input, Space, Typography } from 'antd'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { ChatMessage } from './message'
import { useTranslation } from 'react-i18next'
import { MessageRole, MessageType } from '../../candidate/message'

export type ChatMessageProps = {
    role: MessageRole
    mediaUrl?: string | null
    type?: MessageType
    content: string
    createdAt?: Date | null
}
export type ChatProps = {
    messages: ChatMessageProps[]
    generateUserAnswer?: () => Promise<string>
    onMessageAdded?: (input: string) => void
    disabled?: boolean
    showTyping?: boolean
    placeholder?: string
    className?: string
    noScroll?: boolean
    height?: number
    // Interactive content after the last message, such as reply options.
    footer?: ReactNode
    // Fill the parent height and keep the input at the bottom, instead of a fixed message height.
    fill?: boolean
}

export function Chat({
    messages,
    disabled = false,
    showTyping = true,
    placeholder,
    generateUserAnswer,
    onMessageAdded,
    noScroll = false,
    height = 500,
    footer,
    fill = false,
    className,
    ...rest
}: ChatProps) {
    const { t } = useTranslation()
    const [input, setInput] = useState('')
    const messageContainer = useRef<HTMLElement>(null)
    const previousScrollHeight = useRef(0)

    const userAnswer = useMutation({
        mutationFn: generateUserAnswer,
    })

    useEffect(() => {
        if (noScroll || !messageContainer.current) {
            return
        }

        const el = messageContainer.current
        const threshold = 300
        // Measure against the previous content height so a long reply does not
        // incorrectly look like the user scrolled away from the conversation.
        const isNearBottom = previousScrollHeight.current - el.scrollTop - el.clientHeight < threshold
        previousScrollHeight.current = el.scrollHeight

        if (isNearBottom) {
            el.scrollTo({
                top: el.scrollHeight,
                behavior: 'smooth',
            })
        }
    }, [messages, noScroll])

    const addMessage = (input: string) => {
        if (disabled || !onMessageAdded) {
            return
        }

        onMessageAdded(input)
        setInput('')
    }

    return (
        <Flex
            vertical
            className={clsx(fill && 'min-h-0 flex-1', className)}
            {...rest}
        >
            <Flex
                gap="large"
                vertical
                style={fill ? undefined : { height }}
                className={clsx('overflow-y-auto flex', fill && 'min-h-0 flex-1')}
                ref={messageContainer}
            >
                {messages.map((message, index) => {
                    return (
                        <ChatMessage
                            key={index}
                            {...message}
                        />
                    )
                })}
                {footer}
            </Flex>

            {disabled && showTyping && <Typography.Text type="secondary">{t("I'm typing")}...</Typography.Text>}

            {onMessageAdded && (
                <Space.Compact
                    block
                    size="large"
                    className="mt-4"
                >
                    {generateUserAnswer && (
                        <Button
                            loading={userAnswer.isPending}
                            onClick={() => {
                                userAnswer.mutateAsync().then((message) => {
                                    addMessage(message)
                                })
                            }}
                            icon={<ExperimentOutlined />}
                        />
                    )}
                    <Input
                        className="rounded-md"
                        value={input}
                        onKeyDown={(event) => {
                            if (event.key !== 'Enter' || disabled) {
                                return
                            }
                            event.preventDefault()
                            event.stopPropagation()

                            addMessage(input)
                        }}
                        onChange={(event) => setInput(event.target.value)}
                        placeholder={placeholder}
                        suffix={
                            <Button
                                type="text"
                                size="small"
                                disabled={disabled}
                                onClick={() => {
                                    addMessage(input)
                                }}
                            >
                                <RightOutlined />
                            </Button>
                        }
                    />
                </Space.Compact>
            )}
        </Flex>
    )
}
